// SocialFeedPage — Social feed / copy trading
// Layout: composer, posts, comments, like, copy-trade CTA

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar, TierPill } from '../../components/Brand';

interface Post {
  id: string;
  author: string;
  authorAvatar: string;
  authorTier: 'Beginner' | 'Mid' | 'Professional';
  time: string;
  type: 'trade' | 'milestone' | 'note' | 'bot' | 'milestone-bot';
  content: string;
  trade?: { pair: string; direction: 'long' | 'short'; pnl: number; pnlPct: number };
  bot?: { name: string; emoji: string; color: string };
  likes: number;
  comments: number;
  reposts: number;
  liked: boolean;
  image?: { emoji: string; color: string; label: string };
}

const POSTS: Post[] = [
  {
    id: 'p1', author: 'Marcus Chen',  authorAvatar: 'user-1', authorTier: 'Professional', time: '2h',
    type: 'trade', content: 'NQ breakout setup fired at 18,245. Bot entered long, took 80 ticks, TP hit at 18,325. Clean execution.',
    trade: { pair: 'NQ', direction: 'long', pnl: 895, pnlPct: 0.24 },
    likes: 124, comments: 18, reposts: 8, liked: true,
  },
  {
    id: 'p2', author: 'Sofia Petrov', authorAvatar: 'user-2', authorTier: 'Professional', time: '4h',
    type: 'milestone', content: 'Just hit 100 consecutive trades with 74% win rate on my new mean reversion strategy! 🎉 Thanks to everyone who subscribed.',
    likes: 287, comments: 42, reposts: 15, liked: false,
  },
  {
    id: 'p3', author: 'Aiden Wright', authorAvatar: 'user-3', authorTier: 'Professional', time: '6h',
    type: 'bot', content: 'Just published a new bot: NQ Sniper. It\'s a momentum scalper optimized for the US session. Free for the first 100 subscribers!',
    bot: { name: 'NQ Sniper', emoji: '🎯', color: '#FF3B30' },
    likes: 198, comments: 35, reposts: 22, liked: true,
  },
  {
    id: 'p4', author: 'Yuki Tanaka',  authorAvatar: 'user-4', authorTier: 'Mid', time: '8h',
    type: 'note', content: 'Quick tip: Always set your stop loss BEFORE entering a trade. The market doesn\'t care about your feelings. Risk management = survival.',
    image: { emoji: '💡', color: '#FF9500', label: 'Trading tip' },
    likes: 412, comments: 27, reposts: 89, liked: false,
  },
  {
    id: 'p5', author: 'Lena Voss',    authorAvatar: 'user-4', authorTier: 'Mid', time: '1d',
    type: 'trade', content: 'BTC ranging tight, grid bot caught 14 micro-moves overnight. Compounded gains are wild.',
    trade: { pair: 'BTC', direction: 'long', pnl: 320, pnlPct: 0.64 },
    likes: 89, comments: 12, reposts: 4, liked: true,
  },
  {
    id: 'p6', author: 'Aisha Khan',   authorAvatar: 'user-2', authorTier: 'Mid', time: '1d',
    type: 'milestone', content: 'Reached Professional tier! Been grinding for 6 months. To everyone just starting — keep showing up.',
    likes: 645, comments: 78, reposts: 32, liked: true,
  },
];

export function SocialFeedPage() {
  const nav = useNavigate();
  const [posts, setPosts] = useState(POSTS);
  const [composing, setComposing] = useState(false);

  function toggleLike(id: string) {
    setPosts((curr) => curr.map((p) => p.id === id ? { ...p, liked: !p.liked, likes: p.liked ? p.likes - 1 : p.likes + 1 } : p));
  }

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Feed</div>
        <button onClick={() => alert('Notifications')} className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10 21a2 2 0 0 0 4 0" /></svg>
        </button>
      </div>

      {/* Composer */}
      <div className="mx-4 mt-3 flex gap-2.5">
        <UserAvatar size={40} name="Brandon" />
        <button onClick={() => setComposing(true)} data-testid="compose" className="flex-1 rounded-full bg-white border border-tf-border px-4 text-left text-[14px] text-tf-text-3 active:opacity-40">Share a trade or insight...</button>
      </div>

      {/* Sort tabs */}
      <div className="px-4 pt-3 flex items-center gap-1.5 overflow-x-auto">
        {['Following', 'Top traders', 'New', 'Nearby'].map((t, i) => (
          <button
            key={t}
            data-testid={`feed-tab-${t}`}
            className={['shrink-0 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition',
              i === 0 ? 'bg-tf-text-1 text-white' : 'bg-white border border-tf-border text-tf-text-1 active:opacity-40'
            ].join(' ')}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Posts */}
      <div className="mx-4 mt-3 space-y-2" data-testid="feed-posts">
        {posts.map((p) => (
          <div key={p.id} className="ios26-card p-3" data-testid={`post-${p.id}`}>
            <div className="flex items-start gap-3">
              <button onClick={() => nav('/portal/trader/' + p.id)}>
                <UserAvatar size={40} name={p.author} />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <button onClick={() => nav('/portal/trader/' + p.id)} className="text-[14px] font-bold text-tf-text-1">{p.author}</button>
                  <TierPill tier={p.authorTier} />
                  <span className="text-[11px] text-tf-text-3">· {p.time}</span>
                </div>
                <p className="mt-1 text-[14px] text-tf-text-1 leading-relaxed">{p.content}</p>

                {/* Trade card */}
                {p.trade && (
                  <div className="mt-2 flex items-center gap-2 rounded-[12px] border border-tf-border bg-tf-surface-1 p-2.5">
                    <div className={['flex h-8 w-8 items-center justify-center rounded-[8px] text-[11px] font-bold',
                      p.trade.direction === 'long' ? 'bg-[#34C759]/15 text-tf-profit' : 'bg-[#FF3B30]/15 text-tf-loss'].join(' ')}>
                      {p.trade.pair}
                    </div>
                    <div className="flex-1 text-[12px]">
                      <div className="font-semibold text-tf-text-1">{p.trade.pair} · {p.trade.direction.toUpperCase()}</div>
                      <div className={['tabular-nums', p.trade.pnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                        {p.trade.pnl >= 0 ? '+' : ''}${Math.abs(p.trade.pnl)} · {p.trade.pnlPct.toFixed(2)}%
                      </div>
                    </div>
                    <button onClick={() => alert('Copy trade opened in your bot')} data-testid={`copy-${p.id}`} className="h-8 rounded-full bg-tf-blue px-3 text-[11px] font-semibold text-white active:opacity-40">Copy</button>
                  </div>
                )}

                {/* Bot card */}
                {p.bot && (
                  <div className="mt-2 flex items-center gap-2 rounded-[12px] border border-tf-border bg-tf-surface-1 p-2.5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[18px]" style={{ backgroundColor: p.bot.color + '18' }}>{p.bot.emoji}</div>
                    <div className="flex-1 text-[12px]">
                      <div className="font-semibold text-tf-text-1">{p.bot.name}</div>
                      <div className="text-tf-text-3">New bot from {p.author.split(' ')[0]}</div>
                    </div>
                    <button onClick={() => nav('/portal/bot-marketplace')} className="h-8 rounded-full bg-tf-blue px-3 text-[11px] font-semibold text-white active:opacity-40">View</button>
                  </div>
                )}

                {/* Image */}
                {p.image && (
                  <div className="mt-2 flex items-center gap-3 rounded-[12px] p-3" style={{ backgroundColor: p.image.color + '15' }}>
                    <div className="text-[36px]">{p.image.emoji}</div>
                    <div>
                      <div className="text-[14px] font-bold" style={{ color: p.image.color }}>{p.image.label}</div>
                      <div className="text-[11px] text-tf-text-1">{p.author} shared an insight</div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-2 flex items-center justify-between text-tf-text-3">
                  <button onClick={() => alert('Open comments')} className="flex items-center gap-1.5 text-[12px] active:opacity-40" data-testid={`comment-${p.id}`}>
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    {p.comments}
                  </button>
                  <button onClick={() => alert('Reposted')} className="flex items-center gap-1.5 text-[12px] active:opacity-40" data-testid={`repost-${p.id}`}>
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17 1l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    {p.reposts}
                  </button>
                  <button onClick={() => toggleLike(p.id)} className={['flex items-center gap-1.5 text-[12px] active:opacity-40', p.liked ? 'text-tf-loss' : ''].join(' ')} data-testid={`like-${p.id}`}>
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill={p.liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    {p.likes}
                  </button>
                  <button onClick={() => alert('Share')} className="flex items-center gap-1.5 text-[12px] active:opacity-40" data-testid={`share-${p.id}`}>
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Compose modal */}
      {composing && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm" onClick={() => setComposing(false)}>
          <div className="ios26-sheet w-full max-w-[420px]" onClick={(e) => e.stopPropagation()}>
            <h2 className="px-4 pb-2 text-[20px] font-bold tracking-tight text-tf-text-1">New post</h2>
            <textarea
              placeholder="Share a trade, insight, or question..."
              data-testid="post-text"
              autoFocus
              rows={5}
              className="w-full rounded-[12px] border border-tf-border bg-white p-3 text-[14px] text-tf-text-1 focus:outline-none focus:ring-2 focus:ring-tf-blue/30"
            />
            <div className="mt-2 flex items-center gap-1.5">
              {['📈', '🤖', '🏆', '💡'].map((e) => (
                <button key={e} onClick={() => alert('Add ' + e)} className="flex h-9 w-9 items-center justify-center rounded-full bg-white border border-tf-border text-[16px] active:opacity-40">{e}</button>
              ))}
              <button onClick={() => alert('Attach trade')} className="ml-auto h-9 rounded-full bg-tf-blue/10 px-3 text-[12px] font-semibold text-tf-blue active:opacity-40">+ Attach trade</button>
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setComposing(false)} className="flex-1 h-12 rounded-full bg-tf-surface-1 text-[15px] font-semibold text-tf-text-1 active:opacity-40">Cancel</button>
              <button onClick={() => { alert('Posted!'); setComposing(false); }} data-testid="post-submit" className="flex-1 h-12 btn-spring">Post</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
